import { Check, Edit, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from './Button';
import { TextInput } from './TextInput';
import { cn } from '../lib/cn';

export interface InlineEditorProps {
  /** Floating label on the summary line (e.g. `Address`). */
  label?: string;
  /** Read-only summary string shown when collapsed. */
  summary: string;
  /** Body of the expanded panel — typically a small grid of inputs. */
  children: ReactNode;
  /** Controlled open state. Falls back to internal state when omitted. */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the pencil edit icon (when an external trigger drives it). */
  showInlineEditIcon?: boolean;
  /** Hide the green-tick "confirm" affordance in the footer. */
  showSaveButton?: boolean;
  /**
   * When `false`, hide the read-only summary input + pencil at the top
   * and render the expanded panel directly (always open). Useful when
   * the parent is composing this component inside its own create / edit
   * panel that already provides cancel / save buttons — the summary line
   * would just be visual noise in that context.
   *
   * Defaults to `true` for backward compat (existing summary-then-expand
   * pattern stays unchanged).
   */
  showSummaryInput?: boolean;
  /** Hide the InlineEditor's footer (X + Save) entirely. */
  showFooter?: boolean;
  /** Optional left-side delete affordance in the footer. */
  onDelete?: () => void;
  /** Override label/title used by aria-labels. */
  ariaSubject?: string;
  className?: string;
}

/**
 * Summary-line + expand-panel wrapper. The collapsed view shows a read-only
 * `<TextInput>` with a pencil button on the right; clicking either flips
 * the panel open and shows `children` (typically a small input grid).
 *
 * Lifted from the Old AddressInlineEditor / RegionalSettingsEditor /
 * InvoiceConfigurationInlineEditor pattern — they all repeat the same
 * scaffold around different inner forms, so we factor it once.
 *
 * The component is presentation-only: it doesn't snapshot the previous
 * value on cancel, so callers wanting "discard changes" semantics should
 * stash a snapshot in their own state when `onOpenChange(true)` fires.
 */
export function InlineEditor({
  label,
  summary,
  children,
  isOpen: isOpenProp,
  onOpenChange,
  showInlineEditIcon = true,
  showSaveButton = true,
  showSummaryInput = true,
  showFooter = true,
  onDelete,
  ariaSubject,
  className,
}: InlineEditorProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? !!isOpenProp : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const subject = ariaSubject ?? label ?? 'value';

  // When the summary input is hidden, the panel must always be rendered
  // — there's nothing else to interact with otherwise. Treat as
  // permanently open regardless of `isOpen`.
  const panelOpen = showSummaryInput ? isOpen : true;

  return (
    <div className={cn('space-y-2', className)}>
      {showSummaryInput && (
        <div className="relative">
          <TextInput
            label={label}
            name={`${subject}-summary`}
            autoComplete="off"
            value={summary}
            readOnly
            onClick={() => setOpen(true)}
            className={cn('cursor-pointer', showInlineEditIcon && 'pr-8')}
          />
          {showInlineEditIcon && (
            <button
              type="button"
              aria-label={`Edit ${subject}`}
              onClick={() => setOpen(!isOpen)}
              className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {panelOpen && (
        <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          {children}

          {showFooter && (
            <div className="flex items-center justify-between gap-2">
              <div>
                {onDelete && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      onDelete();
                      setOpen(false);
                    }}
                    className="text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  aria-label="Cancel"
                  onClick={() => setOpen(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                {showSaveButton && (
                  <button
                    type="button"
                    aria-label="Confirm"
                    onClick={() => setOpen(false)}
                    className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
